<template>
  <div>
    <form-section :title="$t('Integrations_Settings_BasicSettings')">
      <rest-form-item
        :label="$t('Integrations_Field_Name')"
        :hint="$t('Integrations_Field_Name_Description')"
        prop="name"
      >
        <el-input
          v-model="model.name"
          :placeholder="$t('Integrations_Field_Name')"
        />
      </rest-form-item>
    </form-section>

    <form-section :title="$t('Integrations_Otp_SMS_Settings')">
      <div class="form-columns">
        <div class="form-column">
          <rest-form-item
            :label="$t('Integrations_Otp_Field_Type')"
            prop="params.type"
          >
            <enum-select
              v-model="model.params.type"
              :options="OtpTypeEnum"
              style="width: 50%"
            />
          </rest-form-item>
          <rest-form-item
            :label="$t('Integrations_Otp_Field_Length')"
            prop="params.length"
          >
            <el-input-number
              v-model="model.params.length"
              :min="4"
              :max="10"
              style="width: 50%"
            />
          </rest-form-item>
        </div>
        <div class="form-column">
          <rest-form-item
            :label="$t('Integrations_Otp_Field_Lifetime')"
            prop="params.lifetime_value"
            class="lifetime-fields"
          >
            <div class="field-row-lifetime-value">
              <el-input-number
                v-model="model.params.lifetime_value"
                :min="1"
                style="width: 50%"
              />
              <rest-form-item
                prop="params.lifetime_unit"
                class="field-row-lifetime-unit"
              >
                <enum-select
                  v-model="model.params.lifetime_unit"
                  :options="OtpLifetimeUnitEnum"
                  style="width: 79%"
                />
              </rest-form-item>
            </div>
          </rest-form-item>
          <rest-form-item
            :label="$t('Integrations_Otp_Field_VerificationTries')"
            prop="params.verification_tries"
          >
            <el-input-number
              v-model="model.params.verification_tries"
              :min="1"
              style="width: 50%"
            />
          </rest-form-item>
        </div>
      </div>
    </form-section>
    <form-section :title="$t('Integrations_Otp_SMS_Content_Title')">
      <rest-form-item>
        <otp-sms-content v-model="model.params.sms" />
      </rest-form-item>
    </form-section>
    <form-section :title="$t('Integrations_Otp_Other_Title')">
      <el-col :span="12">
        <rest-form-item
          prop="params.other_settings.notify_on_error"
          :label="$t('Integrations_Otp_OtherSettings_NotifyOnError_Title')"
          :description="$t('Integrations_Otp_OtherSettings_NotifyOnError_Desc')"
        >
          <feature-toggle
            v-model="model.params.other_settings.notify_on_error"
          />
        </rest-form-item>
        <rest-form-item
          v-if="model.params.other_settings.notify_on_error"
          prop="params.other_settings.error_emails"
          :rules="emailValidation"
        >
          <el-select
            v-model="model.params.other_settings.error_emails"
            multiple
            filterable
            allow-create
            default-first-option
            :placeholder="$t('Integrations_Otp_OtherSettings_Email')"
          />
        </rest-form-item>
      </el-col>
    </form-section>
  </div>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import RestFormItem from '@/common/components/rest/RestFormItem'
import { OtpLifetimeUnitEnum } from '@/modules/integrations/types/otp/enums/OtpLifetimeUnitEnum'
import { OtpTypeEnum } from '@/modules/integrations/types/otp/enums/OtpTypeEnum'
import EnumSelect from '@/common/components/form/EnumSelect.vue'
import FeatureToggle from '@/common/components/form/FeatureToggle.vue'
import OtpSmsContent from './OtpSmsContent.vue'

export default {
  name: 'OtpSettings',

  components: {
    EnumSelect,
    FormSection,
    RestFormItem,
    OtpSmsContent,
    FeatureToggle
  },

  props: {
    model: {
      required: true,
      type: Object
    }
  },

  created() {
    if (this.isCreatePage) {
      this.model.params = {
        length: 6,
        lifetime_value: 1,
        verification_tries: 1,
        sms: {
          from: '',
          text: ''
        },
        other_settings: {
          notify_on_error: 0,
          error_emails: ''
        }
      }
    } else {
      if (!this.model.params.sms) {
        this.$set(this.model.params, 'sms', { from: '', text: '' })
      }
      if (!this.model.params.other_settings) {
        this.$set(this.model.params, 'other_settings', {
          notify_on_error: 0,
          error_emails: ''
        })
      }
    }
  },

  data() {
    return {
      OtpLifetimeUnitEnum,
      OtpTypeEnum,
      rules: {
        name: {
          required: true,
          message: this.$t('RestForm_FieldIsRequired')
        },
        params: {
          type: [
            {
              required: true,
              message: this.$t('RestForm_FieldIsRequired')
            }
          ],
          length: [
            {
              required: true,
              message: this.$t('RestForm_FieldIsRequired')
            }
          ],
          lifetime_value: [
            {
              required: true,
              message: this.$t('RestForm_FieldIsRequired')
            }
          ],
          lifetime_unit: [
            {
              required: true,
              message: this.$t('RestForm_FieldIsRequired')
            }
          ],
          sms: {
            from: [
              {
                required: true,
                message: this.$t('RestForm_FieldIsRequired')
              }
            ],
            text: [
              {
                required: true,
                message: this.$t('RestForm_FieldIsRequired')
              },
              {
                validator: this.validateOtpShortcode
              }
            ]
          }
        }
      },
      emailValidation: {
        validator: this.validateEmail
      }
    }
  },

  methods: {
    validateOtpShortcode(rule, value, callback) {
      if (!value || !/\{\{\s*OTP_CODE\s*\}\}/.test(value)) {
        callback(
          new Error(this.$t('Integrations_Otp_SMS_Text_Shortcode_Error'))
        )
        return
      }
      callback()
    },

    validateEmail(rule, value, callback) {
      if (!value || value.length === 0) {
        return callback(new Error(this.$t('RestForm_FieldIsRequired')))
      }

      if (value.length > 10) {
        return callback(new Error(this.$t('OTP_Settings_TooManyEmails')))
      }

      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
      let emails = Array.isArray(value) ? value : [value]
      let invalidEmail = emails.find(email => !emailPattern.test(email))

      if (invalidEmail) {
        return callback(new Error(this.$t('OTP_Settings_EmailValidation')))
      }
      callback()
    }
  },

  computed: {
    isCreatePage() {
      return !this.$route.query.id
    }
  }
}
</script>
<style scoped>
.form-columns {
  display: flex;
  gap: 30px;
}

.form-column {
  width: 50%;
}

.field-row-lifetime-value {
  display: flex;
  align-items: center;
  gap: 20px;
}

.lifetime-fields {
  display: flex;
  flex-direction: column;
}

.field-row-lifetime-unit {
  flex: 1;
}
</style>



// WEBPACK FOOTER //
// src/modules/integrations/types/otp/OtpSettings.vue